import type { IProjectAsset } from "../../../../../../interfaces";

export const MAX_FILE_SIZE = 20 * 1024 * 1024;

export interface IAttachmentUpload {
    name: string;
    percent: number;
    status: "pending" | "uploading" | "done" | "error";
}

/**
 * Collapse a batch into a single bar and caption.
 *
 * The percentage tracks bytes leaving the browser, so it reaches 100 while the
 * server is still storing the file — hence the separate "Processing" wording.
 */
export function summarizeUploads(uploads: IAttachmentUpload[]) {
    if (!uploads.length) return null;

    const percent = Math.round(uploads.reduce((sum, upload) => sum + upload.percent, 0) / uploads.length);
    const settled = uploads.filter((upload) => upload.status !== "pending" && upload.status !== "uploading");
    const position =
        uploads.length > 1 ? ` (${Math.min(settled.length + 1, uploads.length)} of ${uploads.length})` : "";

    const active = uploads.find((upload) => upload.status === "uploading");
    if (!active) {
        return { percent, label: `Finishing up${position}` };
    }

    return {
        percent,
        label: `${active.percent >= 100 ? "Processing" : "Uploading"} ${active.name}${position}`,
    };
}

export function formatFileSize(bytes: number | null) {
    if (bytes === null || bytes === undefined) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Secondary line under an attachment title: size, who added it, and when. */
export function describeAttachment(asset: IProjectAsset) {
    const parts = [formatFileSize(asset.SizeBytes)];
    if (asset.UploadedByName) {
        parts.push(asset.UploadedByName);
    }
    if (asset.CreatedAt) {
        parts.push(new Date(asset.CreatedAt).toLocaleDateString());
    }
    return parts.filter(Boolean).join(" · ");
}
